import { useMember } from '@features/members/hooks/useMembers';
import { styled } from '@mui/joy';
import { formatDateWithLabel } from '@root/src/utils/formatters/dates';
import { Locale } from '@root/src/utils/formatters/languages';
import FetchError from '@services/api/utils/FetchError';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React from 'react';

export type PublicationInfoProps = {
  updatedAt?: Date;
  createdAt: Date;
  memberId: string;
};

const PublicationInfo = ({ updatedAt, createdAt, memberId }: PublicationInfoProps) => {
  const { t, i18n } = useTranslation();
  const { data: { name } = {}, isLoading, isError, error } = useMember(memberId);
  const isDeletedMember = isError && error instanceof FetchError && error.response?.status === 404;

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const formattedDate = formatDateWithLabel(updatedAt, createdAt, i18n.language as Locale, t);
  const byLabel = t('label.by');

  if (isLoading) {
    return null;
  }

  if (isDeletedMember) {
    return <Legend>{`${formattedDate} ${byLabel} ${t('api.error.memberDeleted')}`}</Legend>;
  }

  if (isError || !name) {
    return <Legend>{`${formattedDate} ${byLabel} -`}</Legend>;
  }

  return (
    <Legend>
      {`${formattedDate} ${byLabel} `}
      <Link href={`/directory/member/${memberId}`} passHref>
        <LinkText onClick={stopPropagation}>{name}</LinkText>
      </Link>
    </Legend>
  );
};
export default PublicationInfo;

const Legend = styled('div')(({ theme: { palette, typography } }) => ({
  ...typography['legend-regular'],
  color: palette.grey4,
}));

const LinkText = styled('span')(({ theme: { palette, typography } }) => ({
  ...typography['legend-regular'],
  color: palette.bluePrimary,
  textDecoration: 'underline',
}));
